// src/pages/Wallet/hooks/useTokenManagement.js
import { useState, useEffect, useContext, useCallback } from 'react';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { useTranslation } from 'react-i18next';

/**
 * Hook to manage custom tokens and token visibility
 * Handles adding, removing, and hiding tokens
 */
export const useTokenManagement = () => {
  const { t } = useTranslation();
  const { currentChain } = useContext(NetworkContext);

  // Token management state with localStorage persistence
  const [hiddenTokenAddresses, setHiddenTokenAddresses] = useState(() => {
    try {
      const saved = localStorage.getItem('hiddenTokenAddresses');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.error('Error loading hidden tokens from localStorage:', e);
      return {};
    }
  });
  
  const [customTokens, setCustomTokens] = useState(() => {
    try {
      const saved = localStorage.getItem('customTokens');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.error('Error loading custom tokens from localStorage:', e);
      return {};
    }
  });

  // Current chain values
  const currentChainKey = currentChain?.key;
  const currentChainCustomTokens = customTokens[currentChainKey] || [];
  const currentChainHiddenTokens = hiddenTokenAddresses[currentChainKey] || [];

  // Toggle token visibility
  const handleToggleTokenVisibility = useCallback((tokenAddress) => {
    if (!tokenAddress || !currentChain) return;
    
    const chainKey = currentChain.key;
    const lowercaseAddress = tokenAddress.toLowerCase();
    
    setHiddenTokenAddresses(prev => {
      // Initialize chain object if it doesn't exist
      const chainTokens = prev[chainKey] || [];
      
      let updatedChainTokens;
      if (chainTokens.includes(lowercaseAddress)) {
        // Remove from hidden list
        updatedChainTokens = chainTokens.filter(addr => addr !== lowercaseAddress);
      } else {
        // Add to hidden list
        updatedChainTokens = [...chainTokens, lowercaseAddress];
      }
      
      return {
        ...prev,
        [chainKey]: updatedChainTokens
      };
    });
  }, [currentChain]);

  // Add custom token
  const handleAddCustomToken = useCallback(async (tokenInfo) => {
    if (!tokenInfo || !tokenInfo.address || !currentChain) return;
    
    const chainKey = currentChain.key;
    const lowercaseAddress = tokenInfo.address.toLowerCase();
    
    // Check if token already exists in custom tokens for this chain
    const chainCustomTokens = customTokens[chainKey] || [];
    const existsInCustomTokens = chainCustomTokens.some(t => 
      t.token && t.token.address && 
      t.token.address.toLowerCase() === lowercaseAddress
    );
    
    // If exists in custom tokens
    if (existsInCustomTokens) {
      // If hidden, unhide it
      const chainHiddenTokens = hiddenTokenAddresses[chainKey] || [];
      if (chainHiddenTokens.includes(lowercaseAddress)) {
        handleToggleTokenVisibility(tokenInfo.address);
        alert(t('wallet.tokenUnhidden', 'This token was hidden. It is now visible in your wallet.'));
      } else {
        alert(t('wallet.tokenAlreadyExists', 'This token is already in your wallet'));
      }
      return;
    }
    
    // Format the new token in the same structure as other tokens
    const newToken = {
      token: {
        type: "ERC-20",
        address: tokenInfo.address,
        name: tokenInfo.name,
        symbol: tokenInfo.symbol,
        decimals: tokenInfo.decimals,
        isCustom: true, // Mark as custom token
        chainKey: chainKey // Store which chain this token belongs to
      },
      value: tokenInfo.balance || "0"
    };
    
    setCustomTokens(prev => {
      // Initialize chain array if it doesn't exist
      const chainTokens = prev[chainKey] || [];
      
      return {
        ...prev,
        [chainKey]: [...chainTokens, newToken]
      };
    });
  }, [currentChain, customTokens, hiddenTokenAddresses, handleToggleTokenVisibility, t]);

  // Remove custom token
  const handleRemoveCustomToken = useCallback((tokenAddress) => {
    if (!tokenAddress || !currentChain) return;
    
    const chainKey = currentChain.key;
    const lowercaseAddress = tokenAddress.toLowerCase();
    
    // Confirm before removing
    const confirmRemove = window.confirm(t('wallet.confirmRemoveToken', 'Are you sure you want to remove this token from your wallet?'));
    if (!confirmRemove) return;
    
    setCustomTokens(prev => {
      const chainTokens = prev[chainKey] || [];
      const updatedChainTokens = chainTokens.filter(t => 
        !t.token || !t.token.address || 
        t.token.address.toLowerCase() !== lowercaseAddress
      );
      
      return {
        ...prev,
        [chainKey]: updatedChainTokens
      };
    });
    
    // Also remove from hidden list if it's there
    setHiddenTokenAddresses(prev => {
      const chainHiddenTokens = prev[chainKey] || [];
      if (chainHiddenTokens.includes(lowercaseAddress)) {
        const updatedHiddenTokens = chainHiddenTokens.filter(addr => addr !== lowercaseAddress);
        return {
          ...prev,
          [chainKey]: updatedHiddenTokens
        };
      }
      return prev;
    });
  }, [currentChain, t]);

  // Persist hidden token addresses to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('hiddenTokenAddresses', JSON.stringify(hiddenTokenAddresses));
    } catch (e) {
      console.error('Error saving hidden tokens to localStorage:', e);
    }
  }, [hiddenTokenAddresses]);
  
  // Persist custom tokens to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('customTokens', JSON.stringify(customTokens));
    } catch (e) {
      console.error('Error saving custom tokens to localStorage:', e);
    }
  }, [customTokens]);

  // Migration for old format to new format (once on component mount)
  useEffect(() => {
    try {
      // Attempt to migrate customTokens from old format (array) to new format (object by chain)
      const savedCustomTokens = localStorage.getItem('customTokens');
      
      if (savedCustomTokens) {
        const parsedTokens = JSON.parse(savedCustomTokens);
        
        // Check if the saved format is an array (old format)
        if (Array.isArray(parsedTokens) && parsedTokens.length > 0) {
          console.log('Migrating custom tokens from array to object format');
          
          // Create new object structure
          const migratedTokens = {};
          
          // Determine chain for each token or default to current chain
          const defaultChain = currentChain?.key || 'ethereum';
          parsedTokens.forEach(token => {
            const chainKey = token.token?.chainKey || defaultChain;
            if (!migratedTokens[chainKey]) {
              migratedTokens[chainKey] = [];
            }
            migratedTokens[chainKey].push(token);
          });
          
          // Update state and localStorage
          setCustomTokens(migratedTokens);
          localStorage.setItem('customTokens', JSON.stringify(migratedTokens));
        }
      }
      
      // Also migrate hidden token addresses if needed
      const savedHidden = localStorage.getItem('hiddenTokenAddresses');
      
      if (savedHidden) {
        const parsedHidden = JSON.parse(savedHidden);
        
        // Check if the saved format is an array (old format)
        if (Array.isArray(parsedHidden) && parsedHidden.length > 0) {
          console.log('Migrating hidden tokens from array to object format');
          
          // Create new object structure - assign to current chain
          const defaultChain = currentChain?.key || 'ethereum';
          const migratedHidden = {
            [defaultChain]: parsedHidden
          };
          
          // Update state and localStorage
          setHiddenTokenAddresses(migratedHidden);
          localStorage.setItem('hiddenTokenAddresses', JSON.stringify(migratedHidden));
        }
      }
    } catch (e) {
      console.error('Error during token data migration:', e);
    }
  }, [currentChain]); 

  return {
    hiddenTokenAddresses,
    customTokens,
    currentChainCustomTokens,
    currentChainHiddenTokens,
    handleToggleTokenVisibility,
    handleAddCustomToken,
    handleRemoveCustomToken
  };
};